import type { Customer } from 'app/shared/models/customer';
import type { LocationDetails } from 'app/shared/models/location-details';
import { ApiUnitsRow, UnitCategoryType } from 'app/shared/models/units';
import type { FlexMonitorStatusResponse } from 'app/shared/models/flex-monitor-status';
import type { MonitorDiagnosticLog } from 'app/shared/services/monitor-diagnostics.service';
import type {
    AlarmDevice,
    FlexMonitorConfiguration,
} from 'app/shared/components/location-card/store/configs/flex/models';
import type { SensorConfigurationCollection } from 'app/shared/components/location-card/store/models/dynamic-section-models';
import type {
    FlexConfiguredSensorView,
    FlexDateContext,
    FlexDiagnosticAngleRowView,
    FlexDiagnosticAngleView,
    FlexDiagnosticReadingRowView,
    FlexDiagnosticReadingView,
    FlexDiagnosticTableView,
    FlexGaugeConfiguration,
    FlexGaugeState,
    FlexInstallationDrawingData,
    FlexSensorDiagnostic,
} from './flex-monitor-status.models';

type FlexDiagnosticLogReading = MonitorDiagnosticLog['readings'][number];

export const FLEX_SIGNAL_GAUGE_CONFIGURATION: FlexGaugeConfiguration = {
    minimum: -115,
    maximum: -20,
    fairMinimum: -94,
    goodMinimum: -85,
};

export const FLEX_BATTERY_GAUGE_CONFIGURATION: FlexGaugeConfiguration = {
    minimum: 0,
    maximum: 13,
    fairMinimum: 7,
    goodMinimum: 8,
};

const FLEX_ACCELEROMETER_SENSOR = 'Accelerometer';
const FLEX_ANGLE_SENSOR_DEVICES = ['AvMax', 'AvGated', 'ParaDepth', 'ParaFlow'];
const FLEX_ANGLE_WARNING_DEGREES = 5;
const FLEX_ANGLE_PRECISION = 1;
const FLEX_DIAGNOSTIC_PRECISION = 3;
const FLEX_DIAGNOSTIC_COMPARISON_MAX_AGE_MS = 30 * 60 * 1000;
const FLEX_DIAGNOSTIC_UNIT_CATEGORIES = new Map<string, UnitCategoryType>([
    ['EntityType_UpDepth', UnitCategoryType.Linear],
    ['EntityType_ParaDepth', UnitCategoryType.Linear],
    ['EntityType_PDepth', UnitCategoryType.Linear],
    ['EntityType_SurfaceVelocity', UnitCategoryType.Velocity],
    ['EntityType_PeakVelocity', UnitCategoryType.Velocity],
    ['EntityType_GatedVelocity', UnitCategoryType.Velocity],
    ['EntityType_AirTemperature', UnitCategoryType.Temperature],
    ['EntityType_AirTemperature2', UnitCategoryType.Temperature],
    ['EntityType_RainGaugeTemperature', UnitCategoryType.Temperature],
    ['EntityType_Temperature', UnitCategoryType.Temperature],
    ['EntityType_WaterTemperature', UnitCategoryType.Temperature],
    ['EntityType_WaterTemperature2', UnitCategoryType.Temperature],
]);

export function selectFlexMp1Location(details: LocationDetails[] | null): LocationDetails | null {
    if (!details?.length) return null;

    const mp1 = details.find((detail) => (detail.monitoringPoint ?? detail.MonitoringPoint) === 1);
    if (mp1) return mp1;

    const hasMonitoringPoint = details.some(
        (detail) => detail.monitoringPoint !== undefined || detail.MonitoringPoint !== undefined,
    );
    return !hasMonitoringPoint && details.length === 1 ? details[0] : null;
}

export function mapFlexConfiguredSensors(
    collection: SensorConfigurationCollection | null,
    status: FlexMonitorStatusResponse | null,
): FlexConfiguredSensorView[] {
    const sensors = (collection?.sensors ?? []).filter((sensor) => sensor.monitoringPoint === 0);
    const reportedId = normalizeSensorId(status?.reportedSensor?.modbusSensorId);
    const reportedMatches = reportedId ? sensors.filter((sensor) => normalizeSensorId(sensor.id) === reportedId) : [];
    const identityAmbiguous = reportedMatches.length > 1;
    const depthSensorId = normalizeSensorId(collection?.mp_1?.depthSensorId);
    const velocitySensorId = normalizeSensorId(collection?.mp_1?.velocitySensorId);

    return sensors.map((configuration) => {
        const sensorId = normalizeSensorId(configuration.id);
        const isReportedSensor = !!sensorId && sensorId === reportedId && reportedMatches.length === 1;

        return {
            configuration,
            sensorId,
            isDepthPreference: !!sensorId && sensorId === depthSensorId,
            isVelocityPreference: !!sensorId && sensorId === velocitySensorId,
            depthPreference: collection?.mp_1?.depthEntityType ?? 'Auto',
            velocityPreference: collection?.mp_1?.velocityEntityType ?? 'Auto',
            physicalSensorSerialNumber: isReportedSensor
                ? status?.reportedSensor?.physicalSensorSerialNumber ?? null
                : null,
            reportedModel: isReportedSensor ? status?.reportedSensor?.model ?? null : null,
            reportedAtUtc: isReportedSensor ? status?.reportedSensor?.reportedAtUtc ?? null : null,
            identityAmbiguous: identityAmbiguous && !!sensorId && sensorId === reportedId,
        };
    });
}

export function mapFlexDiagnosticTable(
    sensors: FlexConfiguredSensorView[],
    diagnostics: FlexSensorDiagnostic[],
    units: ApiUnitsRow[] | null,
): FlexDiagnosticTableView | null {
    if (!sensors.length) return null;

    const sensor =
        sensors.find((item) => diagnostics.some((diagnostic) => isFlexDiagnosticSensor(item, diagnostic))) ??
        sensors[0];
    const diagnostic = diagnostics.find((item) => isFlexDiagnosticSensor(sensor, item));
    const previousReadings = diagnostic?.previousLog?.readings ?? [];
    const latestReadings = diagnostic?.log?.readings ?? [];
    const previousAccelerometer = findFlexAccelerometerReading(previousReadings);
    const latestAccelerometer = findFlexAccelerometerReading(latestReadings);
    const sensorNames = [...previousReadings, ...latestReadings]
        .map((reading) => reading.sensor)
        .filter((name, index, names) => name !== FLEX_ACCELEROMETER_SENSOR && names.indexOf(name) === index);
    const angles = [
        ...mapFlexDiagnosticAngleRow(
            'DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR_SLOPE',
            previousAccelerometer?.accelerometerSlope,
            latestAccelerometer?.accelerometerSlope,
        ),
        ...mapFlexDiagnosticAngleRow(
            'DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR_ROTATION',
            previousAccelerometer?.accelerometerRotation,
            latestAccelerometer?.accelerometerRotation,
        ),
    ];
    if (!sensorNames.length && !angles.length) return null;

    return {
        sensor,
        previous: diagnostic?.previousLog
            ? {
                  labelKey: 'DIAGNOSTICS.FLEX_MONITOR_STATUS.PREVIOUS_RESULT',
                  state: 'completed',
                  completedAt: diagnostic.previousLog.status.currentMonitorTime,
              }
            : null,
        latest: diagnostic
            ? {
                  labelKey:
                      diagnostic.commandId === null
                          ? 'DIAGNOSTICS.FLEX_MONITOR_STATUS.LAST_RESULT'
                          : 'DIAGNOSTICS.FLEX_MONITOR_STATUS.NEW_RESULT',
                  state: diagnostic.state,
                  completedAt: diagnostic.log?.status.currentMonitorTime ?? null,
              }
            : null,
        readings: sensorNames.map((name) => mapFlexDiagnosticReadingRow(name, previousReadings, latestReadings, units)),
        angles,
    };
}

export function getFlexMp1Alarm(configuration: FlexMonitorConfiguration | null): AlarmDevice | null {
    if (!configuration) return null;

    const alarm = configuration.devices.find((device) => device.installationNumber === 0 && 'lowLevelEnable' in device);
    if (!alarm || !('lowLevelEnable' in alarm)) return null;

    return alarm;
}

export function classifyFlexSignal(value: number | null | undefined): FlexGaugeState {
    return classifyFlexGauge(value, FLEX_SIGNAL_GAUGE_CONFIGURATION);
}

export function classifyFlexBattery(value: number | null | undefined): FlexGaugeState {
    return classifyFlexGauge(value, FLEX_BATTERY_GAUGE_CONFIGURATION);
}

export function hasFlexInstallationDrawingData(data: FlexInstallationDrawingData | null): boolean {
    const entries = data?.cosmos.pipe?.e;
    if (!entries || entries.length < 2) return false;

    const linearUnit = data.units.find((unit) => unit.unitCategory === UnitCategoryType.Linear);
    const areaUnit = data.units.find((unit) => unit.unitCategory === UnitCategoryType.Area);
    if (!linearUnit || !areaUnit || !Number.isFinite(linearUnit.factor) || !Number.isFinite(areaUnit.factor)) {
        return false;
    }

    return (
        entries.every(
            (entry) =>
                Number.isFinite(entry.d) &&
                Number.isFinite(entry.a) &&
                Number.isFinite(entry.p) &&
                Number.isFinite(entry.c),
        ) && entries.some((entry) => entry.d > 0 && entry.c > 0)
    );
}

function classifyFlexGauge(value: number | null | undefined, configuration: FlexGaugeConfiguration): FlexGaugeState {
    if (
        value === null ||
        value === undefined ||
        !Number.isFinite(value) ||
        value < configuration.minimum ||
        value > configuration.maximum
    ) {
        return 'unknown';
    }
    if (value >= configuration.goodMinimum) return 'good';
    if (value >= configuration.fairMinimum) return 'fair';
    return 'poor';
}

export function normalizeSensorId(value: number | string | null | undefined): string | null {
    if (value === null || value === undefined) return null;

    const normalized = String(value).trim();
    if (!/^\d+$/.test(normalized)) return null;

    return normalized.replace(/^0+(?=\d)/, '');
}

export function formatFlexStatusTimestamp(value: string | null | undefined, context: FlexDateContext | null): string {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const customer = context?.customer;
    const timeZone = context?.timeZone;
    if (customer && timeZone) {
        if (customer.isDaylightSavings && timeZone.ianaValue) {
            return formatDateParts(date, customer, timeZone.ianaValue);
        }

        const offset = parseUtcOffset(timeZone.utcValue);
        if (offset !== null) {
            return formatDateParts(new Date(date.getTime() + offset), customer, 'UTC');
        }
    }

    return `${formatDateParts(date, customer ?? null, 'UTC')} UTC`;
}

export function formatFlexHydrographTimestamp(timestampSeconds: number, customer: Customer | null): string {
    if (!Number.isFinite(timestampSeconds)) return '';
    return formatDateParts(new Date(timestampSeconds * 1000), customer, 'UTC');
}

export function formatFlexDiagnosticTimestamp(value: Date | null, customer: Customer | null): string {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return formatFlexHydrographTimestamp(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes()) / 1000,
        customer,
    );
}

export function isRecentFlexDiagnostic(
    value: Date | string | null | undefined,
    context: FlexDateContext | null,
    requestedAt: number,
): boolean {
    if (!value || !Number.isFinite(requestedAt)) return false;

    const isWithinWindow = (completedAt: number): boolean => {
        const age = requestedAt - completedAt;
        return age >= 0 && age <= FLEX_DIAGNOSTIC_COMPARISON_MAX_AGE_MS;
    };
    let wallTime: number;
    if (typeof value === 'string') {
        const suffix = /(Z|[+-]\d{2}:\d{2})$/.exec(value)?.[1];
        const clockValue = suffix ? value.slice(0, -suffix.length) : value;
        if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?$/.test(clockValue)) return false;
        const normalized =
            clockValue.slice(0, 19) + '.' + (clockValue.split('.')[1] ?? '').padEnd(3, '0').slice(0, 3) + 'Z';
        const date = new Date(normalized);
        if (!Number.isFinite(date.getTime()) || date.toISOString() !== normalized) return false;
        wallTime = date.getTime();
        if (suffix) {
            const offset = suffix === 'Z' ? 0 : parseUtcOffset(`UTC${suffix}`);
            return offset !== null && isWithinWindow(wallTime - offset);
        }
    } else {
        wallTime = Date.UTC(
            value.getFullYear(),
            value.getMonth(),
            value.getDate(),
            value.getHours(),
            value.getMinutes(),
            value.getSeconds(),
            value.getMilliseconds(),
        );
    }
    if (!Number.isFinite(wallTime) || !context?.timeZone) return false;

    if (!context.customer.isDaylightSavings) {
        const offset = parseUtcOffset(context.timeZone.utcValue);
        return offset !== null && isWithinWindow(wallTime - offset);
    }
    if (!context.timeZone.ianaValue) return false;

    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: context.timeZone.ianaValue,
            timeZoneName: 'longOffset',
        });
        const offsetAt = (timestamp: number): number | null => {
            const offset = formatter.formatToParts(new Date(timestamp)).find((part) => part.type === 'timeZoneName');
            return offset ? parseUtcOffset(offset.value.replace(/^GMT/, 'UTC')) : null;
        };
        const day = 24 * 60 * 60 * 1000;
        const offsets = new Set(
            [
                requestedAt,
                requestedAt - FLEX_DIAGNOSTIC_COMPARISON_MAX_AGE_MS,
                requestedAt - day,
                requestedAt + day,
            ].map(offsetAt),
        );
        const candidates: number[] = [];
        for (const offset of offsets) {
            if (offset === null) continue;
            const completedAt = wallTime - offset;
            if (offsetAt(completedAt) === offset) candidates.push(completedAt);
        }
        return candidates.length === 1 && isWithinWindow(candidates[0]);
    } catch {
        return false;
    }
}

export function formatFlexCoordinate(value: number | null | undefined): string {
    if (!Number.isFinite(value) || value === null || value === undefined) return '';
    return new Intl.NumberFormat('en-US', { maximumSignificantDigits: 6, useGrouping: false }).format(value);
}

function isFlexDiagnosticSensor(sensor: FlexConfiguredSensorView, diagnostic: FlexSensorDiagnostic): boolean {
    return !!sensor.sensorId && sensor.sensorId === normalizeSensorId(diagnostic.modbusSensorId);
}

function mapFlexDiagnosticReadingRow(
    sensor: string,
    previousReadings: FlexDiagnosticLogReading[],
    latestReadings: FlexDiagnosticLogReading[],
    units: ApiUnitsRow[] | null,
): FlexDiagnosticReadingRowView {
    const unitCategory = FLEX_DIAGNOSTIC_UNIT_CATEGORIES.get(sensor);
    const unit = units?.find((item) => item.unitCategory === unitCategory);
    const precision = unit?.precision ?? FLEX_DIAGNOSTIC_PRECISION;
    const previous = mapFlexDiagnosticReading(previousReadings.find((reading) => reading.sensor === sensor));
    const latest = mapFlexDiagnosticReading(latestReadings.find((reading) => reading.sensor === sensor));

    return {
        sensor: sensor.replace(/^EntityType_/, ''),
        unit: unit?.unitSuffix ?? null,
        precision,
        previous,
        latest,
        change: getFlexDiagnosticChange(previous?.value, latest?.value, precision),
    };
}

function mapFlexDiagnosticReading(reading: FlexDiagnosticLogReading | undefined): FlexDiagnosticReadingView | null {
    if (reading?.reading === null || reading?.reading === undefined || !Number.isFinite(reading.reading)) return null;

    return {
        value: reading.reading,
        quality: reading.quality ?? null,
        gain: reading.gain ?? null,
        maximumSignal: reading.maximumSignal ?? null,
    };
}

function findFlexAccelerometerReading(readings: FlexDiagnosticLogReading[]): FlexDiagnosticLogReading | undefined {
    return readings.find(
        (reading) => reading.sensor === FLEX_ACCELEROMETER_SENSOR && FLEX_ANGLE_SENSOR_DEVICES.includes(reading.device),
    );
}

function mapFlexDiagnosticAngleRow(
    labelKey: string,
    previousDegrees: number | null | undefined,
    latestDegrees: number | null | undefined,
): FlexDiagnosticAngleRowView[] {
    const previous = mapFlexAngle(previousDegrees);
    const latest = mapFlexAngle(latestDegrees);
    if (!previous && !latest) return [];

    return [
        {
            labelKey,
            precision: FLEX_ANGLE_PRECISION,
            previous,
            latest,
            change: getFlexDiagnosticChange(previous?.degrees, latest?.degrees, FLEX_ANGLE_PRECISION),
        },
    ];
}

function mapFlexAngle(degrees: number | null | undefined): FlexDiagnosticAngleView | null {
    if (degrees === null || degrees === undefined || !Number.isFinite(degrees)) return null;
    return { degrees, isOutsideRange: Math.abs(degrees) > FLEX_ANGLE_WARNING_DEGREES };
}

function getFlexDiagnosticChange(
    previous: number | undefined,
    latest: number | undefined,
    precision: number,
): number | null {
    if (previous === undefined || latest === undefined) return null;

    const change = roundFlexValue(roundFlexValue(latest, precision) - roundFlexValue(previous, precision), precision);
    return change === 0 ? null : change;
}

function roundFlexValue(value: number, precision: number): number {
    return Number(value.toFixed(precision));
}

function parseUtcOffset(value: string): number | null {
    if (value === 'UTC') return 0;

    const match = /^UTC([+-])(\d{2}):(\d{2})$/.exec(value);
    if (!match || Number(match[2]) > 23 || Number(match[3]) > 59) return null;

    const minutes = Number(match[2]) * 60 + Number(match[3]);
    if (!Number.isFinite(minutes)) return null;
    return (match[1] === '-' ? -minutes : minutes) * 60 * 1000;
}

function formatDateParts(date: Date, customer: Customer | null, timeZone: string): string {
    const hour12 = !customer?.timeFormat || customer.timeFormat === 'h:mm:ss tt';
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12,
    });
    const parts = formatter.formatToParts(date);
    const values = new Map<Intl.DateTimeFormatPartTypes, string>(parts.map((part) => [part.type, part.value]));
    const year = values.get('year') ?? '';
    const month = values.get('month') ?? '';
    const day = values.get('day') ?? '';
    const hour = values.get('hour') ?? '';
    const minute = values.get('minute') ?? '';
    const dayPeriod = values.get('dayPeriod');
    const dateValue =
        customer?.dateFormat === 'DD/MM/YYYY'
            ? `${day}/${month}/${year}`
            : customer?.dateFormat === 'YYYY/MM/DD'
            ? `${year}/${month}/${day}`
            : `${month}/${day}/${year}`;
    return `${dateValue} ${hour}:${minute}${dayPeriod ? ` ${dayPeriod}` : ''}`;
}
